import { z } from 'zod';
import { apiClient as baseApiClient } from '#/api/client.tsx';
import {
  AccountDocumentFileViewUrlSchema,
  AccountDocumentSchema,
  ApproveDrivingLicenseInfoSchema,
  ApproveInsurancePolicyInfoSchema,
  ApproveVehicleRegistrationInfoSchema,
  PageExtraSchema,
} from '../model.ts';
import type {
  ApproveAccountDocumentParams,
  DocumentType,
  ReviewState,
} from '../model.ts';

const BASE = '/account-documents';

const apiClient = baseApiClient.create({
  baseURL: `${import.meta.env.VITE_BASE_URL}/cockpit`,
});

const buildApproveBody = (params: ApproveAccountDocumentParams) => {
  switch (params.documentType) {
    case 'VEHICLE_REGISTRATION':
      return {
        vehicleRegistrationInfo: ApproveVehicleRegistrationInfoSchema.parse(
          params.info,
        ),
      };
    case 'DRIVING_LICENSE':
      return {
        drivingLicenseInfo: ApproveDrivingLicenseInfoSchema.parse(params.info),
      };
    case 'INSURANCE_POLICY':
      return {
        insurancePolicyInfo: ApproveInsurancePolicyInfoSchema.parse(
          params.info,
        ),
      };
  }
};

type ListAccountDocumentsParams = {
  accountId?: string;
  reviewState?: ReviewState;
  documentType?: DocumentType;
  page?: number;
  pageSize?: number;
};

const getAccountDocuments = async (params: ListAccountDocumentsParams = {}) => {
  const response = await apiClient.get(BASE, { params });
  return {
    data: z.array(z.unknown()).parse(response.data.data),
    extra: PageExtraSchema.parse(response.data.extra),
  };
};

const getAccountDocumentDetails = async (id: string) => {
  const response = await apiClient.get(`${BASE}/${id}`);
  return AccountDocumentSchema.parse(response.data.data);
};

const getDocumentViewUrl = async (fileId: string) => {
  const response = await apiClient.get(`${BASE}/files/${fileId}/view-url`);
  return AccountDocumentFileViewUrlSchema.parse(response.data.data);
};

const approveAccountDocument = async (
  id: string,
  params: ApproveAccountDocumentParams,
) => {
  const body = buildApproveBody(params);
  await apiClient.post(`${BASE}/${id}/approval`, body);
};

const rejectAccountDocument = async (id: string, reason: string) => {
  const trimmedReason = reason.trim();
  if (!trimmedReason) {
    throw new Error('reason 不可空白');
  }

  await apiClient.post(`${BASE}/${id}/rejection`, { reason: trimmedReason });
};

export type { ListAccountDocumentsParams };

export {
  getAccountDocuments,
  getAccountDocumentDetails,
  getDocumentViewUrl,
  approveAccountDocument,
  rejectAccountDocument,
};
